type MetricType = 'timing' | 'count' | 'gauge';

interface Metric {
  name: string;
  type: MetricType;
  value: number;
  tags?: Record<string, string>;
  timestamp?: number;
}

class Monitoring {
  private static instance: Monitoring;
  private metrics: Metric[] = [];
  private errorCount = 0;
  private readonly maxErrors = 100;
  private readonly flushInterval = 60000; // 1 minute
  private readonly maxQueueSize = 1000;

  private constructor() {
    this.startAutoFlush();
    this.setupErrorHandling();
    this.setupPerformanceMonitoring();
  }

  static getInstance(): Monitoring {
    if (!Monitoring.instance) {
      Monitoring.instance = new Monitoring();
    }
    return Monitoring.instance;
  }

  private startAutoFlush() {
    setInterval(() => this.flush(), this.flushInterval);
  }

  private setupErrorHandling() {
    window.addEventListener('error', event => {
      this.recordError(event.error);
    });

    window.addEventListener('unhandledrejection', event => {
      this.recordError(event.reason);
    });
  }

  private setupPerformanceMonitoring() {
    if ('PerformanceObserver' in window) {
      // Monitor long tasks
      const longTaskObserver = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          this.recordMetric({
            name: 'long_task',
            type: 'timing',
            value: entry.duration,
            tags: { entryType: entry.entryType },
          });
        });
      });

      longTaskObserver.observe({ entryTypes: ['longtask'] });

      // Monitor resource timing
      const resourceObserver = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          if (entry.entryType === 'resource') {
            const resource = entry as PerformanceResourceTiming;
            this.recordMetric({
              name: 'resource_timing',
              type: 'timing',
              value: resource.duration,
              tags: {
                type: resource.initiatorType,
                url: resource.name,
              },
            });
          }
        });
      });

      resourceObserver.observe({ entryTypes: ['resource'] });
    }
  }

  recordMetric(metric: Omit<Metric, 'timestamp'>) {
    if (this.metrics.length >= this.maxQueueSize) {
      this.flush();
    }

    this.metrics.push({
      ...metric,
      timestamp: Date.now(),
    });
  }

  recordError(error: Error, context?: Record<string, any>) {
    if (this.errorCount >= this.maxErrors) {
      console.warn('Error limit reached, dropping error:', error);
      return;
    }

    this.errorCount++;

    const errorData = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      context,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Log error details
    console.error('Application error:', errorData);

    // Record error metric
    this.recordMetric({
      name: 'error_count',
      type: 'count',
      value: 1,
      tags: {
        error_type: error.name,
        error_message: error.message,
      },
    });
  }

  recordTiming(name: string, duration: number, tags?: Record<string, string>) {
    this.recordMetric({
      name,
      type: 'timing',
      value: duration,
      tags,
    });
  }

  private async flush() {
    if (this.metrics.length === 0) return;

    const metricsToSend = [...this.metrics];
    this.metrics = [];
    this.errorCount = 0;

    try {
      // Group metrics by type for better analysis
      const groupedMetrics = metricsToSend.reduce(
        (acc, metric) => {
          if (!acc[metric.type]) {
            acc[metric.type] = [];
          }
          acc[metric.type].push(metric);
          return acc;
        },
        {} as Record<MetricType, Metric[]>
      );

      // Here you would normally send metrics to your monitoring service
      console.log('Flushing metrics:', groupedMetrics);
    } catch (error) {
      console.error('Failed to flush metrics:', error);
      // Re-queue failed metrics
      this.metrics = [...metricsToSend, ...this.metrics].slice(0, this.maxQueueSize);
    }
  }

  measurePerformance<T>(name: string, fn: () => T): T {
    const start = performance.now();
    try {
      return fn();
    } finally {
      const duration = performance.now() - start;
      this.recordTiming(name, duration);
    }
  }

  async measureAsyncPerformance<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    try {
      return await fn();
    } finally {
      const duration = performance.now() - start;
      this.recordTiming(name, duration);
    }
  }
}

export const monitoring = Monitoring.getInstance();
